// A libxml2-compatible HTML parser + serializer, scoped to what the save path
// needs (docs/26 §9 M3, doc 32 §1.5 — the rich-editor save port).
//
// PHP's clear_html() is a DOMDocument round trip: loadHTML() (libxml2's HTML4
// parser, recovery mode) → transform → saveHTML() → html_entity_decode(
// ENT_QUOTES|ENT_XML1). Node has no libxml: parse5 implements the HTML5 spec,
// whose implied-element and recovery rules differ from libxml's exactly where
// this matters. So the behaviors below were PROBED against the live PHP stack
// and encoded here (gate: npm run check:save-text-parity):
//
//   * an implied <p> wraps body content only when the FIRST thing in the body
//     is a text node — a leading inline ELEMENT is not wrapped, and text after
//     a block element is not wrapped either;
//   * head-only tags (script/style/meta/title/link/base) seen before any body
//     content are hoisted into <head>, where the whitelist walk never reaches
//     them — which is why a LEADING <script> survives clear_html while one
//     between body nodes is dropped. Preserved 1:1;
//   * a block start tag closes an open <p>; <li> closes an open <li>;
//   * saveHTML entity-encodes text and ordinary attribute values, but
//     URI-escapes href/action/src (and name on <a>) — `é` → `%C3%A9`,
//     `"` → `%22`. The trailing ENT_XML1 decode then restores & < > " ' as raw
//     characters, so those never appear escaped in the stored HTML.
//
// Anything genuinely exotic (frames, implied table sections, deep recovery) may
// diverge from libxml; that is the accepted deviation of the doc-26 §12 decision
// to serve every save from TS. Divergence changes only how OUR OWN stored HTML
// is cleaned — no cross-stack contract reads these bytes.

import { decodeParserEntities, encodeSurvivingEntities } from './htmlEntities'
import autoCloseJson from '../fixtures/html_autoclose.json'

export type HtmlNode = HtmlElement | HtmlText | HtmlComment

export interface HtmlElement {
  kind: 'element'
  name: string
  attrs: HtmlAttr[]
  children: HtmlNode[]
}

export interface HtmlAttr {
  name: string
  value: string
}

export interface HtmlText {
  kind: 'text'
  value: string
}

export interface HtmlComment {
  kind: 'comment'
  value: string
}

export interface HtmlDocument {
  /** Nodes libxml keeps outside <html> (leading comments). */
  leading: HtmlNode[]
  head: HtmlNode[]
  body: HtmlNode[]
}

// libxml HTML4 void elements (html40ElementTable, EMPTY content model).
const VOID_TAGS = new Set([
  'area', 'base', 'basefont', 'br', 'col', 'frame', 'hr', 'img', 'input',
  'isindex', 'link', 'meta', 'param',
])

// Hoisted into <head> while the body is still empty.
const HEAD_TAGS = new Set(['script', 'style', 'meta', 'title', 'link', 'base'])

// libxml's htmlStartClose table: which OPEN tags each start tag implicitly
// closes. Probed pairwise off the live stack rather than transcribed from
// libxml source (fixtures/html_autoclose.json, regenerate with
// scripts/parity/gen_html_autoclose.php). It is not a simple block/inline
// split — <p> closes an open <b>, but <div> does not.
const AUTO_CLOSE: Record<string, string[]> = autoCloseJson as Record<string, string[]>

function closedBy(startTag: string): Set<string> {
  return new Set(AUTO_CLOSE[startTag] ?? [])
}

// Raw-text elements: their content is not markup.
const RAW_TEXT_TAGS = new Set(['script', 'style'])

// libxml omits the end tag of an EMPTY element whose end tag is optional
// (html40ElementTable saveEndTag). Swept across the whole clear_html whitelist
// against the live stack: `li` is the only member — <p></p> keeps its close tag.
const OPTIONAL_END_TAGS = new Set(['li'])

export function isVoidTag(name: string): boolean {
  return VOID_TAGS.has(name)
}

export function element(name: string, attrs: HtmlAttr[] = [], children: HtmlNode[] = []): HtmlElement {
  return { kind: 'element', name, attrs, children }
}

export function text(value: string): HtmlText {
  return { kind: 'text', value }
}

export function getAttr(el: HtmlElement, name: string): string | null {
  const found = el.attrs.find(a => a.name === name)
  return found ? found.value : null
}

export function hasAttr(el: HtmlElement, name: string): boolean {
  return el.attrs.some(a => a.name === name)
}

export function removeAttr(el: HtmlElement, name: string): void {
  el.attrs = el.attrs.filter(a => a.name !== name)
}

/** DOMElement::setAttribute — updates in place (keeping position) or appends. */
export function setAttr(el: HtmlElement, name: string, value: string): void {
  const found = el.attrs.find(a => a.name === name)
  if (found) found.value = value
  else el.attrs.push({ name, value })
}

// ---------------------------------------------------------------------------
// Parse

const TAG_START = /^<([a-zA-Z][^\s/>]*)/
const END_TAG = /^<\/\s*([a-zA-Z][^\s>]*)\s*>?/
const ATTR = /^\s*([^\s=/>]+)(?:\s*=\s*("[^"]*"|'[^']*'|[^\s>]*))?/

export function parseHtml(source: string): HtmlDocument {
  const doc: HtmlDocument = { leading: [], head: [], body: [] }
  const stack: HtmlElement[] = []
  let bodyStarted = false

  const top = (): HtmlElement | null => (stack.length ? stack[stack.length - 1] : null)

  const appendToBody = (node: HtmlNode): void => {
    const parent = top()
    if (parent) {
      parent.children.push(node)
      return
    }
    // Body level.
    if (node.kind === 'comment' && !bodyStarted) {
      // libxml keeps a comment seen before any content outside <body>; the
      // whitelist walk never reaches it, so it survives clear_html.
      doc.leading.push(node)
      return
    }
    if (node.kind === 'text') {
      let value = node.value
      if (!bodyStarted) {
        // Leading document whitespace is dropped by the parser.
        value = value.replace(/^[ \t\n\r\f]+/, '')
        if (value === '') return
        // Character data at the start of the body implies a <p>.
        const implied = element('p')
        doc.body.push(implied)
        stack.push(implied)
        bodyStarted = true
        implied.children.push(text(value))
        return
      }
      doc.body.push(text(value))
      return
    }
    doc.body.push(node)
    if (node.kind === 'element') bodyStarted = true
  }

  const openElement = (el: HtmlElement): void => {
    // htmlAutoClose: pop every open element this start tag implicitly closes,
    // walking outward from the innermost (libxml stops at the first element it
    // does NOT close, so a <p> inside <b> inside <div> closes only the <b>).
    const closes = closedBy(el.name)
    while (stack.length && closes.has(stack[stack.length - 1].name)) stack.pop()

    if (!stack.length && !bodyStarted && HEAD_TAGS.has(el.name)) {
      doc.head.push(el)
      if (!isVoidTag(el.name)) stack.push(el)
      return
    }

    appendToBody(el)
    if (!isVoidTag(el.name)) stack.push(el)
  }

  const closeElement = (name: string): void => {
    for (let i = stack.length - 1; i >= 0; i--) {
      if (stack[i].name === name) {
        stack.length = i
        return
      }
    }
    // No matching open element: libxml drops the stray end tag.
  }

  let i = 0
  while (i < source.length) {
    const ch = source[i]

    if (ch !== '<') {
      const next = source.indexOf('<', i)
      const end = next === -1 ? source.length : next
      appendToBody(text(decodeParserEntities(source.slice(i, end))))
      i = end
      continue
    }

    const rest = source.slice(i)

    if (rest.startsWith('<!--')) {
      const end = source.indexOf('-->', i + 4)
      const stop = end === -1 ? source.length : end + 3
      const value = source.slice(i + 4, end === -1 ? source.length : end)
      appendToBody({ kind: 'comment', value })
      i = stop
      continue
    }

    if (rest.startsWith('<!') || rest.startsWith('<?')) {
      // DOCTYPE / processing instruction: dropped from the output anyway.
      const end = source.indexOf('>', i)
      i = end === -1 ? source.length : end + 1
      continue
    }

    const endMatch = END_TAG.exec(rest)
    if (endMatch) {
      closeElement(endMatch[1].toLowerCase())
      i += endMatch[0].length
      continue
    }

    const startMatch = TAG_START.exec(rest)
    if (!startMatch) {
      // A '<' that opens nothing is literal text (libxml recovery).
      appendToBody(text('<'))
      i += 1
      continue
    }

    const name = startMatch[1].toLowerCase()
    let cursor = startMatch[0].length
    const attrs: HtmlAttr[] = []

    while (cursor < rest.length) {
      const tail = rest.slice(cursor)
      const close = /^\s*\/?>/.exec(tail)
      if (close) {
        cursor += close[0].length
        break
      }
      const attrMatch = ATTR.exec(tail)
      if (!attrMatch || !attrMatch[1]) {
        cursor += 1
        continue
      }
      const attrName = attrMatch[1].toLowerCase()
      let value = attrMatch[2] ?? ''
      if (value.startsWith('"') || value.startsWith("'")) value = value.slice(1, -1)
      // Duplicate attributes: the FIRST wins (libxml ignores later ones).
      if (!attrs.some(a => a.name === attrName)) {
        attrs.push({ name: attrName, value: decodeParserEntities(value) })
      }
      cursor += attrMatch[0].length
    }

    const el = element(name, attrs)
    openElement(el)
    i += cursor

    if (RAW_TEXT_TAGS.has(name) && !isVoidTag(name)) {
      // Raw text content: consume verbatim up to the matching end tag.
      const closeRe = new RegExp(`</${name}\\s*>`, 'i')
      const tail = source.slice(i)
      const m = closeRe.exec(tail)
      const raw = m ? tail.slice(0, m.index) : tail
      if (raw) el.children.push(text(raw))
      i += m ? m.index + m[0].length : tail.length
      closeElement(name)
    }
  }

  return doc
}

// ---------------------------------------------------------------------------
// Serialize (saveHTML + the trailing ENT_XML1 decode, folded together)

// libxml URI-escapes href/action/src (and name on <a>): everything outside this
// set becomes %XX of its UTF-8 bytes. Derived byte-by-byte from the live stack.
const URI_KEEP = new Set([
  ...'!#$%&\'()*+,-./0123456789:;<=>?@ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz~',
])
// '$' '[' '\' ']' '^' '`' '{' '|' '}' and space ARE escaped; '&' and '<' survive
// (libxml escapes them as entities, which the ENT_XML1 decode then restores).
for (const ch of '$[\\]^`{|}') URI_KEEP.delete(ch)

function uriEscapeAttr(value: string): string {
  let out = ''
  for (const ch of value) {
    if (URI_KEEP.has(ch)) {
      out += ch
      continue
    }
    for (const byte of Buffer.from(ch, 'utf8')) {
      out += '%' + byte.toString(16).toUpperCase().padStart(2, '0')
    }
  }
  return out
}

function isUriAttr(el: HtmlElement, name: string): boolean {
  if (name === 'href' || name === 'action' || name === 'src') return true
  return name === 'name' && el.name === 'a'
}

function serializeAttrs(el: HtmlElement): string {
  let out = ''
  for (const attr of el.attrs) {
    const value = isUriAttr(el, attr.name)
      ? uriEscapeAttr(attr.value)
      : encodeSurvivingEntities(attr.value)
    out += ` ${attr.name}="${value}"`
  }
  return out
}

export function serializeNodes(nodes: HtmlNode[]): string {
  let out = ''
  for (const node of nodes) {
    if (node.kind === 'text') {
      out += encodeSurvivingEntities(node.value)
      continue
    }
    if (node.kind === 'comment') {
      out += `<!--${node.value}-->`
      continue
    }
    out += `<${node.name}${serializeAttrs(node)}>`
    if (isVoidTag(node.name)) continue
    if (!node.children.length && OPTIONAL_END_TAGS.has(node.name)) continue
    out += RAW_TEXT_TAGS.has(node.name)
      ? node.children.map(c => (c.kind === 'text' ? c.value : '')).join('')
      : serializeNodes(node.children)
    out += `</${node.name}>`
  }
  return out
}

export function serializeDocument(doc: HtmlDocument): string {
  return serializeNodes(doc.leading) + serializeNodes(doc.head) + serializeNodes(doc.body)
}
